import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { type Columns, DataTable } from '../components/ui/data-table';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { can, useMe } from '../lib/session';
import type { PendingRefund, ReturnDetail, ReturnRow, ReturnStatus } from '../lib/types';
import { formatDateTime, formatMoney, parseMoney } from '../lib/utils';
import { LocationSelect, locationsQuery } from './warehouse/stock';

const STATUSES: ReturnStatus[] = [
  'requested',
  'approved',
  'in_transit',
  'received',
  'refunded',
  'rejected',
  'closed',
];

export function ReturnsPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ReturnStatus | ''>('');
  const [openId, setOpenId] = useState<string>();
  const [error, setError] = useState<string>();
  const list = useQuery({
    queryKey: ['returns', status],
    queryFn: () => api<{ items: ReturnRow[] }>(`/returns${status ? `?status=${status}` : ''}`),
  });
  const pending = useQuery({
    queryKey: ['returns', 'pending-refunds'],
    queryFn: () => api<{ items: PendingRefund[] }>('/refunds/pending'),
    enabled: can(me, 'payments.read'),
  });

  const columns = useMemo<Columns<ReturnRow>>(
    () => [
      {
        id: 'number',
        header: t('ret.number'),
        accessorFn: (r) => r.number,
        cell: ({ row }) => (
          <button
            type="button"
            className="font-medium hover:underline"
            onClick={() => {
              setOpenId(row.original.id);
            }}
          >
            {row.original.number}
          </button>
        ),
      },
      { id: 'order', header: t('po.order'), accessorFn: (r) => r.orderNumber },
      { id: 'email', header: 'Email', accessorFn: (r) => r.email },
      { id: 'items', header: t('ret.items'), accessorFn: (r) => r.items },
      {
        id: 'kind',
        header: t('ret.kind'),
        accessorFn: (r) => (r.isWithdrawal ? t('ret.withdrawal') : t('ret.other')),
      },
      {
        id: 'status',
        header: t('po.status'),
        accessorFn: (r) => r.status,
        cell: ({ row }) => (
          <Badge variant={row.original.status === 'requested' ? 'warning' : 'secondary'}>
            {t(`ret.statuses.${row.original.status}`)}
          </Badge>
        ),
      },
      {
        id: 'requested',
        header: t('ret.requestedAt'),
        accessorFn: (r) => r.requestedAt,
        cell: ({ row }) => formatDateTime(row.original.requestedAt, i18n.language),
      },
    ],
    [t, i18n.language],
  );

  const confirmRefund = async (id: string) => {
    setError(undefined);
    try {
      await api(`/refunds/${id}/confirm`, { method: 'POST' });
      await queryClient.invalidateQueries({ queryKey: ['returns'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <PageHeader title={t('ret.title')} description={t('ret.hint')} />
      {error ? (
        <Alert variant="destructive" className="mb-4">
          {error}
        </Alert>
      ) : null}
      {pending.data?.items.length ? (
        <div className="mb-6 grid gap-2">
          <h2 className="text-lg font-semibold">{t('ret.pendingRefunds')}</h2>
          <p className="text-sm text-muted-foreground">{t('ret.pendingRefundsHint')}</p>
          {pending.data.items.map((r) => (
            <div
              key={r.id}
              className="flex flex-wrap items-center gap-3 rounded-md border p-3 text-sm"
            >
              <span className="font-semibold">{r.orderNumber}</span>
              <span>{r.email}</span>
              <span>{formatMoney(r.amountMinor, r.currency, i18n.language)}</span>
              <Badge variant="outline">{r.provider}</Badge>
              <span className="text-muted-foreground">{r.reason}</span>
              {can(me, 'orders.refund') ? (
                <Button size="sm" className="ml-auto" onClick={() => void confirmRefund(r.id)}>
                  {t('ret.confirmRefund')}
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      <div className="mb-4">
        <Select
          aria-label={t('po.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ReturnStatus | '');
          }}
          className="w-56"
        >
          <option value="">{t('po.allStatuses')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`ret.statuses.${s}`)}
            </option>
          ))}
        </Select>
      </div>
      {list.error ? (
        <Alert variant="destructive" className="mb-4">
          {errorMessage(list.error)}
        </Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isPending}
        rowKey={(r) => r.id}
      />
      {openId ? (
        <ReturnDialog
          id={openId}
          onClose={() => {
            setOpenId(undefined);
          }}
        />
      ) : null}
    </>
  );
}

function ReturnDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['returns', 'detail', id],
    queryFn: () => api<ReturnDetail>(`/returns/${id}`),
  });
  const locations = useQuery(locationsQuery);
  const [conditions, setConditions] = useState<Record<string, 'resellable' | 'damaged'>>({});
  const [location, setLocation] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [amount, setAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const data = detail.data;
  const locationId = location || locations.data?.items[0]?.id || '';

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(undefined);
    try {
      await api(`/returns/${id}/${path}`, { method: 'POST', body: body ?? {} });
      await queryClient.invalidateQueries({ queryKey: ['returns'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <Dialog open onClose={onClose} title={t('ret.title')}>
        {detail.error ? <Alert variant="destructive">{errorMessage(detail.error)}</Alert> : null}
      </Dialog>
    );
  }
  const money = (minor: number) => formatMoney(minor, data.currency, i18n.language);
  const open =
    data.status === 'requested' || data.status === 'approved' || data.status === 'in_transit';
  const refundMinor = amount ? parseMoney(amount) : data.refund.totalMinor;

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${t('ret.return')} ${data.number}`}
      description={`${t('po.order')} ${data.orderNumber} · ${data.email}`}
      className="max-w-2xl"
    >
      <div className="grid gap-4 text-sm">
        <div className="flex gap-2">
          <Badge variant="secondary">{t(`ret.statuses.${data.status}`)}</Badge>
          <Badge variant="outline">
            {data.isWithdrawal ? t('ret.withdrawal') : t('ret.other')}
          </Badge>
        </div>
        {data.reason ? <p className="text-muted-foreground">{data.reason}</p> : null}
        <table className="w-full">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">{t('wh.sku')}</th>
              <th>{t('wh.item')}</th>
              <th className="text-right">{t('wh.qty')}</th>
              <th className="pl-4">{t('ret.condition')}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((i) => (
              <tr key={i.id} className="border-t">
                <td className="py-1 font-mono">{i.sku}</td>
                <td>
                  {i.name}
                  {i.reason ? (
                    <div className="text-xs text-muted-foreground">{i.reason}</div>
                  ) : null}
                </td>
                <td className="text-right">{i.qty}</td>
                <td className="pl-4">
                  {open && can(me, 'returns.receive') ? (
                    <Select
                      aria-label={`${t('ret.condition')} ${i.sku}`}
                      value={conditions[i.id] ?? 'resellable'}
                      onChange={(e) => {
                        setConditions((c) => ({
                          ...c,
                          [i.id]: e.target.value as 'resellable' | 'damaged',
                        }));
                      }}
                      className="h-8"
                    >
                      <option value="resellable">{t('ret.resellable')}</option>
                      <option value="damaged">{t('ret.damaged')}</option>
                    </Select>
                  ) : i.condition ? (
                    t(`ret.${i.condition}`)
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {error ? <Alert variant="destructive">{error}</Alert> : null}

        {data.status === 'requested' && can(me, 'returns.manage') ? (
          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <Button disabled={busy} onClick={() => void act('approve')}>
              {t('ret.approve')}
            </Button>
            <Input
              aria-label={t('ret.rejectReason')}
              placeholder={t('ret.rejectReason')}
              value={rejectReason}
              onChange={(e) => {
                setRejectReason(e.target.value);
              }}
              className="w-64"
            />
            <Button
              variant="outline"
              disabled={busy || rejectReason.trim().length < 2}
              onClick={() => void act('reject', { reason: rejectReason.trim() })}
            >
              {t('ret.reject')}
            </Button>
          </div>
        ) : null}

        {open && can(me, 'returns.receive') ? (
          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <Field label={t('ret.restockTo')}>
              {(fieldId) => (
                <LocationSelect
                  id={fieldId}
                  value={locationId}
                  onChange={setLocation}
                  allowAll={false}
                />
              )}
            </Field>
            <Button
              disabled={busy}
              onClick={() =>
                void act('receive', {
                  lines: data.items.map((i) => ({
                    returnItemId: i.id,
                    condition: conditions[i.id] ?? 'resellable',
                    ...((conditions[i.id] ?? 'resellable') === 'resellable' ? { locationId } : {}),
                  })),
                })
              }
            >
              {t('ret.receive')}
            </Button>
          </div>
        ) : null}

        {data.status === 'received' && can(me, 'orders.refund') ? (
          <div className="grid gap-2 border-t pt-3">
            <p>
              {t('ret.refundCalc', {
                items: money(data.refund.itemsMinor),
                shipping: money(data.refund.shippingMinor),
                total: money(data.refund.totalMinor),
              })}
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <Field label={t('ret.refundAmount')}>
                {(fieldId) => (
                  <Input
                    id={fieldId}
                    inputMode="decimal"
                    placeholder={(data.refund.totalMinor / 100).toFixed(2)}
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                    }}
                    className="w-32"
                  />
                )}
              </Field>
              {refundMinor !== undefined && refundMinor < data.refund.totalMinor ? (
                <Field label={t('ret.refundReason')} className="flex-1">
                  {(fieldId) => (
                    <Input
                      id={fieldId}
                      value={refundReason}
                      onChange={(e) => {
                        setRefundReason(e.target.value);
                      }}
                    />
                  )}
                </Field>
              ) : null}
              <Button
                disabled={busy || refundMinor === undefined || refundMinor <= 0}
                onClick={() =>
                  void act('refund', {
                    ...(amount && refundMinor !== undefined ? { amountMinor: refundMinor } : {}),
                    ...(refundReason.trim() ? { reason: refundReason.trim() } : {}),
                  })
                }
              >
                {t('ret.refund')}
              </Button>
            </div>
          </div>
        ) : null}
        <p className="text-xs text-muted-foreground">
          {t('ret.requestedAt')}: {formatDateTime(data.requestedAt, i18n.language)}
        </p>
      </div>
    </Dialog>
  );
}
